import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrentUserResponse, Territory } from '@hetivo/hetivo-lib-types';
import { ROLE_SCOPE } from '@hetivo/hetivo-lib-types';
import { useMasterContext } from '&/master';
import { useTerritory } from '../../../settings/territories/hooks/use-territory';
import { useTerritories } from '../../../settings/territories/hooks/use-territories';
import { TerritoryRow } from './territory-row';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DerivedTerritory {
  id: string;
  name: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface TerritoriesPanelProps {
  backendUser: CurrentUserResponse | null;
  canReadTerritories: boolean;
  onBrowseTerritory: (territoryId: string, territoryName: string) => void;
  onClose: () => void;
}

// ─── Internal hook result ─────────────────────────────────────────────────────

interface TerritoriesPanelData {
  displayTotal: number;
  effectiveSiteCountByTerritoryId: Record<string, number>;
  hasMore: boolean;
  territories: DerivedTerritory[];
  total: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveScopedTerritories(
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): DerivedTerritory[] {
  if (!resolvedScopes) return [];
  const territoryMap = new Map<string, DerivedTerritory>();
  for (const resolvedScope of resolvedScopes) {
    if (resolvedScope.level === ROLE_SCOPE.territory) {
      territoryMap.set(resolvedScope.id, { id: resolvedScope.id, name: resolvedScope.name });
    } else if (
      resolvedScope.level === ROLE_SCOPE.location &&
      resolvedScope.parentTerritoryId &&
      resolvedScope.parentTerritoryName
    ) {
      territoryMap.set(resolvedScope.parentTerritoryId, {
        id: resolvedScope.parentTerritoryId,
        name: resolvedScope.parentTerritoryName,
      });
    }
  }
  return Array.from(territoryMap.values());
}

/**
 * Derives site counts per territory from resolvedScopes.
 *
 * - Territory-level scopes: the backend populates `locationCount` on each territory-level resolved scope.
 * - Site-level scopes: counts are aggregated by parentTerritoryId.
 */
function deriveSiteCountsFromResolvedScopes(
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): Record<string, number> {
  if (!resolvedScopes) return {};
  const counts: Record<string, number> = {};
  for (const rs of resolvedScopes) {
    if (rs.level === ROLE_SCOPE.territory && rs.locationCount != null) {
      counts[rs.id] = rs.locationCount;
    } else if (rs.level === ROLE_SCOPE.location && rs.parentTerritoryId) {
      counts[rs.parentTerritoryId] = (counts[rs.parentTerritoryId] ?? 0) + 1;
    }
  }
  return counts;
}

function resolveSiteCountByTerritoryId(
  backendUser: CurrentUserResponse | null,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): Record<string, number> {
  // Prefer the backend-provided locationCountsByTerritory (covers tenant users who have no territory-level
  // resolved scopes), falling back to counts derived from resolvedScopes for backward compatibility.
  return backendUser?.scopeSummary?.locationCountsByTerritory ?? deriveSiteCountsFromResolvedScopes(resolvedScopes);
}

// ─── Data hook ────────────────────────────────────────────────────────────────

function useTerritoriesPanelData(
  backendUser: CurrentUserResponse | null,
  canReadTerritories: boolean,
  currentTerritoryId: string | null,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): TerritoriesPanelData {
  const { data: territoriesData } = useTerritories({ enabled: canReadTerritories, page: 1, pageSize: 10 });
  const { data: currentTerritory } = useTerritory(currentTerritoryId ?? '', canReadTerritories);

  const rawTerritories = territoriesData?.items ?? [];
  const total = territoriesData?.total ?? 0;

  const apiTerritories = React.useMemo(() => {
    if (!currentTerritoryId || !currentTerritory) return rawTerritories;
    const isInList = rawTerritories.some((territory) => territory.id === currentTerritoryId);
    if (isInList) return rawTerritories;
    return [currentTerritory, ...rawTerritories.slice(0, 9)];
  }, [rawTerritories, currentTerritoryId, currentTerritory]);

  const scopeDerivedTerritories = React.useMemo(() => deriveScopedTerritories(resolvedScopes), [resolvedScopes]);
  const siteCountByTerritoryId = React.useMemo(
    () => resolveSiteCountByTerritoryId(backendUser, resolvedScopes),
    [backendUser, resolvedScopes],
  );

  const territories = canReadTerritories ? apiTerritories : scopeDerivedTerritories;
  const displayTotal = canReadTerritories ? total : territories.length;

  return {
    displayTotal,
    effectiveSiteCountByTerritoryId: siteCountByTerritoryId,
    hasMore: total > 10,
    territories,
    total,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TerritoriesPanel({
  backendUser,
  canReadTerritories,
  onBrowseTerritory,
  onClose,
}: TerritoriesPanelProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { territoryId: currentTerritoryId } = useMasterContext();

  const { displayTotal, effectiveSiteCountByTerritoryId, hasMore, territories, total } = useTerritoriesPanelData(
    backendUser,
    canReadTerritories,
    currentTerritoryId,
    backendUser?.resolvedScopes,
  );

  return (
    <div>
      <div className="px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('mainSelector.territories')} ({displayTotal})
        </span>
      </div>

      <div>
        <div className="p-1">
          {territories.map((territory) => (
            <TerritoryRow
              key={territory.id}
              canSelectTerritory={canReadTerritories}
              onBrowseTerritory={onBrowseTerritory}
              onClose={onClose}
              siteCount={effectiveSiteCountByTerritoryId[territory.id] ?? 0}
              territory={territory as unknown as Territory}
            />
          ))}
        </div>
      </div>

      {hasMore && (
        <div className="px-3 py-2">
          <span className="text-[11px] text-muted-foreground">
            {t('mainSelector.searchMoreTerritories', { count: total - 10 })}
          </span>
        </div>
      )}
    </div>
  );
}
